import React, { useEffect, useState } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import "./ReportsPage.css";
import * as XLSX from "xlsx";
import { getAuthToken, getCurrentShopId } from "./auth";

import {
  calculateBusinessSummary,
  /*  isRefundSale,
  parsePaymentBreakdown, */
} from "./reportCalculations";

const REPORT_API_URL =
  process.env.REACT_APP_API_URL || "https://api.deskpos.ie";

function ReportsPage() {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [shopId, setShopId] = useState(null);
  const [shopName, setShopName] = useState("");
  const [tills, setTills] = useState([]);
  const [selectedTillId, setSelectedTillId] = useState("all");
  const [tillsLoading, setTillsLoading] = useState(false);

  const loadShopName = async (activeShopId) => {
    try {
      if (!activeShopId) return;

      const token = getAuthToken();

      const response = await axios.get(`${REPORT_API_URL}/shop-settings`, {
        params: {
          shop_id: activeShopId,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setShopName(
        response.data?.shop_table_name ||
          response.data?.shop_name ||
          response.data?.name ||
          "",
      );
    } catch (err) {
      console.error(
        "Load accounting report shop name error:",
        err.response?.data || err.message,
      );

      setShopName("");
    }
  };

  const loadTills = async () => {
    try {
      setTillsLoading(true);

      const token = getAuthToken();

      const response = await axios.get(`${REPORT_API_URL}/tills`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const tillList = Array.isArray(response.data?.tills)
        ? response.data.tills
        : [];

      setTills(tillList);
    } catch (err) {
      console.error(
        "Load accounting report tills error:",
        err.response?.data || err.message,
      );

      setTills([]);
    } finally {
      setTillsLoading(false);
    }
  };

  useEffect(() => {
    const currentShopId = getCurrentShopId();

    console.log("ReportsPage current admin shop id:", currentShopId);

    setShopId(currentShopId);
    loadTills();
    loadShopName(currentShopId);
  }, []);

  const getBusinessSummary = (targetReport = report) => {
    if (!targetReport) {
      return calculateBusinessSummary([]);
    }

    const salesList = Array.isArray(targetReport.sales)
      ? targetReport.sales
      : [];

    return calculateBusinessSummary(salesList);
  };

  const calculateDrsTotalFromSales = () => {
    if (!Array.isArray(report?.sales)) return 0;

    return report.sales.reduce((saleSum, sale) => {
      let items = [];

      try {
        items =
          typeof sale.items === "string"
            ? JSON.parse(sale.items)
            : sale.items || [];
      } catch {
        items = [];
      }

      const saleDrsTotal = items.reduce((itemSum, item) => {
        return (
          itemSum +
          Number(item.drs_amount || item.drsAmount || 0) * Number(item.qty || 0)
        );
      }, 0);

      return saleSum + saleDrsTotal;
    }, 0);
  };

  const getDrsTotal = () => {
    if (!report) return 0;

    const businessSummary = getBusinessSummary(report);
    const salesTotal = Number(businessSummary.netSales || 0);

    const categoryGrossTotal = Object.values(report.categories || {}).reduce(
      (sum, val) => sum + Number(val.gross || 0),
      0,
    );

    const drsFromBackend = Number(
      report.summary?.drsTotal || report.drsTotal || 0,
    );

    const drsFromSales = Number(calculateDrsTotalFromSales() || 0);

    const drsFromTotals = Math.max(0, salesTotal - categoryGrossTotal);

    return Number(
      (drsFromBackend > 0
        ? drsFromBackend
        : drsFromSales > 0
          ? drsFromSales
          : drsFromTotals
      ).toFixed(2),
    );
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "N/A";

    return new Date(dateValue).toLocaleDateString("en-IE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const normalizeCategoryName = (category) => {
    const clean = String(category || "Uncategorized")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "");

    const categoryMap = {
      softdrink: "SOFT DRINKS",
      softdrinks: "SOFT DRINKS",
      drink: "SOFT DRINKS",
      drinks: "SOFT DRINKS",

      grocery: "GROCERY",
      groceries: "GROCERY",

      tobacco: "TOBACCO",
      tobbaco: "TOBACCO",

      lottery: "LOTTERY",
      lotto: "LOTTERY",

      payzone: "PAYZONE",
      surcharge: "SURCHARGE",
      chilled: "CHILLED",
      crisps: "CRISPS",
      confectionery: "CONFECTIONERY",
    };

    return (
      categoryMap[clean] || String(category || "Uncategorized").toUpperCase()
    );
  };

  const getMergedCategories = () => {
    const mergedCategories = {};

    Object.entries(report?.categories || {}).forEach(([cat, val]) => {
      const normalizedCat = normalizeCategoryName(cat);

      if (!mergedCategories[normalizedCat]) {
        mergedCategories[normalizedCat] = {
          qty: 0,
          vat: 0,
          net: 0,
          gross: 0,
        };
      }

      mergedCategories[normalizedCat].qty += Number(val.qty || 0);
      mergedCategories[normalizedCat].vat += Number(val.vat || 0);
      mergedCategories[normalizedCat].net += Number(val.net || 0);
      mergedCategories[normalizedCat].gross += Number(val.gross || 0);
    });

    return mergedCategories;
  };

  const getSelectedTillLabel = () => {
    if (selectedTillId === "all") {
      return "All Tills";
    }

    const selectedTill = tills.find(
      (till) => String(till.id) === String(selectedTillId),
    );

    if (!selectedTill) {
      return "Selected Till";
    }

    return `${selectedTill.name} (${selectedTill.till_code})`;
  };

  const downloadExcelReport = () => {
    if (!report) return;

    const rows = [];

    const businessSummary = getBusinessSummary(report);

    const grossSales = Number(businessSummary.grossSales || 0);
    const returnTotal = Number(businessSummary.refundTotal || 0);
    const salesTotal = Number(businessSummary.netSales || 0);

    const cashSales = Number(businessSummary.cashSales || 0);
    const cashReturns = Number(businessSummary.cashReturns || 0);
    const cashTotal = Number(businessSummary.netCash || 0);

    const cardSales = Number(businessSummary.cardSales || 0);
    const cardReturns = Number(businessSummary.cardReturns || 0);
    const cardTotal = Number(businessSummary.netCard || 0);

    const saleTransactions = Number(businessSummary.saleTransactions || 0);

    const returnTransactions = Number(businessSummary.refundTransactions || 0);

    const totalTransactions = Number(businessSummary.totalTransactions || 0);

    const drsTotal = getDrsTotal();

    const reportFrom = report.reportFrom || fromDate;
    const reportTo = report.reportTo || toDate;

    const totalDays =
      report.reportDays ||
      (reportFrom && reportTo
        ? Math.floor(
            (new Date(reportTo) - new Date(reportFrom)) / (1000 * 60 * 60 * 24),
          ) + 1
        : 0);

    const fromDisplay = formatDate(reportFrom);
    const toDisplay = formatDate(reportTo);

    rows.push(["MONTHLY ACCOUNTING REPORT"]);
    rows.push([]);
    rows.push(["Shop Name", shopName || `Shop ${shopId || "N/A"}`]);
    rows.push(["Till", getSelectedTillLabel()]);
    rows.push(["Report Period", `${fromDisplay} to ${toDisplay}`]);
    rows.push(["From Date", fromDisplay]);
    rows.push(["To Date", toDisplay]);
    rows.push(["Total Days", totalDays]);
    rows.push([]);

    rows.push(["SALES / RETURNS SUMMARY"]);
    rows.push(["Gross Sales", grossSales.toFixed(2)]);
    rows.push(["Returns / Refunds", `-${returnTotal.toFixed(2)}`]);
    rows.push(["Net Sales", salesTotal.toFixed(2)]);
    rows.push(["DRS Deposit", Number(drsTotal || 0).toFixed(2)]);
    rows.push([
      "Net Sales Excluding DRS",
      (salesTotal - Number(drsTotal || 0)).toFixed(2),
    ]);
    rows.push([]);

    rows.push(["PAYMENT SUMMARY"]);
    rows.push(["Cash Sales", cashSales.toFixed(2)]);
    rows.push(["Cash Returns", `-${cashReturns.toFixed(2)}`]);
    rows.push(["Net Cash", cashTotal.toFixed(2)]);
    rows.push([]);
    rows.push(["Card Sales", cardSales.toFixed(2)]);
    rows.push(["Card Returns", `-${cardReturns.toFixed(2)}`]);
    rows.push(["Net Card", cardTotal.toFixed(2)]);
    rows.push([]);
    rows.push(["Net Payments", (cashTotal + cardTotal).toFixed(2)]);
    rows.push([]);

    rows.push(["TRANSACTIONS"]);
    rows.push(["Sale Transactions", saleTransactions]);
    rows.push(["Return Transactions", returnTransactions]);
    rows.push(["Total Transactions", totalTransactions]);
    rows.push([]);

    rows.push(["DRS BREAKDOWN"]);
    rows.push(["Total DRS Collected", Number(drsTotal || 0).toFixed(2)]);
    rows.push([]);

    rows.push(["VAT BREAKDOWN"]);

    Object.entries(report.vatSummary || {}).forEach(([k, v]) => {
      if (typeof v === "object" && v !== null) {
        rows.push([
          `VAT ${k}`,
          "NET",
          Number(v.net || 0).toFixed(2),
          "VAT",
          Number(v.vat || v.amount || 0).toFixed(2),
          "GROSS",
          Number(v.gross || v.total || 0).toFixed(2),
        ]);
      } else {
        rows.push([`VAT ${k}`, Number(v || 0).toFixed(2)]);
      }
    });

    rows.push([]);

    rows.push(["CATEGORY SUMMARY"]);
    rows.push(["CATEGORY", "QTY", "VAT", "NET", "GROSS"]);

    const mergedCategories = getMergedCategories();

    Object.entries(mergedCategories).forEach(([cat, val]) => {
      rows.push([
        cat,
        val.qty,
        Number(val.vat || 0).toFixed(2),
        Number(val.net || 0).toFixed(2),
        Number(val.gross || 0).toFixed(2),
      ]);
    });

    rows.push([]);

    rows.push(["FINAL TOTALS"]);
    rows.push(["Gross Sales", grossSales.toFixed(2)]);
    rows.push(["Returns / Refunds", `-${returnTotal.toFixed(2)}`]);
    rows.push(["Net Sales", salesTotal.toFixed(2)]);
    rows.push(["DRS Deposit", Number(drsTotal || 0).toFixed(2)]);
    rows.push([
      "Net Sales Excluding DRS",
      (salesTotal - Number(drsTotal || 0)).toFixed(2),
    ]);
    rows.push(["Net Cash", cashTotal.toFixed(2)]);
    rows.push(["Net Card", cardTotal.toFixed(2)]);
    rows.push(["Net Payments", (cashTotal + cardTotal).toFixed(2)]);

    const worksheet = XLSX.utils.aoa_to_sheet(rows);

    worksheet["!cols"] = [
      { wch: 28 },
      { wch: 22 },
      { wch: 16 },
      { wch: 16 },
      { wch: 16 },
      { wch: 16 },
      { wch: 16 },
    ];

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Accounting Report");

    const safeFrom = fromDisplay.replaceAll("/", "-");
    const safeTo = toDisplay.replaceAll("/", "-");

    const tillFileLabel =
      selectedTillId === "all" ? "all-tills" : `till-${selectedTillId}`;

    const safeShopName = String(shopName || `shop-${shopId || "unknown"}`)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

    XLSX.writeFile(
      workbook,
      `accounting-report-${safeShopName}-${tillFileLabel}-${safeFrom}-to-${safeTo}.xlsx`,
    );

    /* setReport(null);
  setFromDate("");
  setToDate(""); */
  };

 const fetchReport = async () => {
  try {
    setError("");

    if (!shopId) {
      setError(
        "Shop is still loading. Please wait."
      );
      return;
    }

    if (!fromDate || !toDate) {
      setError(
        "Please select from and to dates"
      );
      return;
    }

    const token = getAuthToken();

    if (!token) {
      setError(
        "Your login session has expired. Please log in again."
      );
      return;
    }

    const res = await axios.get(
      `${REPORT_API_URL}/reports/monthly`,
      {
        params: {
          shop_id: shopId,
          from: fromDate,
          to: toDate,
          till_id:
            selectedTillId === "all"
              ? "all"
              : Number(selectedTillId),
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    console.log(
      "MONTHLY REPORT FOR SHOP:",
      shopId
    );

    console.log(
      "MONTHLY REPORT DATA:",
      res.data
    );

    const salesList = Array.isArray(
      res.data.sales
    )
      ? res.data.sales
      : [];

    const businessSummary =
      calculateBusinessSummary(salesList);

    console.log(
      "MONTHLY REPORT BUSINESS SUMMARY:",
      businessSummary
    );

    setReport({
      ...res.data,
      businessSummary,
      reportFrom: fromDate,
      reportTo: toDate,
    });
  } catch (err) {
    console.error(
      "Report error:",
      err.response?.data || err.message
    );

    if (err.response?.status === 401) {
      setError(
        "Your login session has expired. Please log in again."
      );
      return;
    }

    setError(
      err.response?.data?.error ||
        "Failed to generate report"
    );
  }
};

  const downloadPDFReport = () => {
    if (!report) return;

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const margin = 12;
    let y = 14;

    const businessSummary = getBusinessSummary(report);

    const grossSales = Number(businessSummary.grossSales || 0);

    const refundTotal = Number(businessSummary.refundTotal || 0);

    const salesTotal = Number(businessSummary.netSales || 0);

    const cashSales = Number(businessSummary.cashSales || 0);

    const cashReturns = Number(businessSummary.cashReturns || 0);

    const cashTotal = Number(businessSummary.netCash || 0);

    const cardSales = Number(businessSummary.cardSales || 0);

    const cardReturns = Number(businessSummary.cardReturns || 0);

    const cardTotal = Number(businessSummary.netCard || 0);

    const saleTransactions = Number(businessSummary.saleTransactions || 0);

    const refundTransactions = Number(businessSummary.refundTransactions || 0);

    const totalTransactions = Number(businessSummary.totalTransactions || 0);

    const drsTotal = getDrsTotal();
    const salesExcludingDrs = salesTotal - drsTotal;

    const mergedCategories = getMergedCategories();

    const money = (value) => {
      return `EUR ${Number(value || 0).toFixed(2)}`;
    };

    const safeText = (value) => {
      return String(value ?? "-");
    };

    const truncate = (value, max = 28) => {
      const text = safeText(value);
      return text.length > max ? `${text.slice(0, max - 3)}...` : text;
    };

    /*  const getShopName = () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      return user.shop_name || user.shopName || "Desk POS";
    } catch {
      return "Desk POS";
    }
  }; */

    const addFooter = () => {
      const totalPages = doc.internal.getNumberOfPages();

      for (let i = 1; i <= totalPages; i += 1) {
        doc.setPage(i);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(120);

        doc.text(
          `Page ${i} of ${totalPages}`,
          pageWidth - margin,
          pageHeight - 8,
          { align: "right" },
        );

        doc.text(
          `Generated: ${new Date().toLocaleString()}`,
          margin,
          pageHeight - 8,
        );
      }

      doc.setTextColor(0);
    };

    const addPageIfNeeded = (space = 15) => {
      if (y + space > pageHeight - 18) {
        doc.addPage();
        y = 14;
      }
    };

    const startNewPage = () => {
      doc.addPage();
      y = 14;
    };

    const drawSectionTitle = (title) => {
      addPageIfNeeded(18);

      y += 4;

      doc.setFillColor(245, 247, 250);
      doc.roundedRect(margin, y, pageWidth - margin * 2, 9, 2, 2, "F");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(25);
      doc.text(title, margin + 4, y + 6);

      y += 14;
    };

    const drawSummaryBox = (label, value, x, boxY, width) => {
      doc.setFillColor(250, 250, 250);
      doc.setDrawColor(225);
      doc.roundedRect(x, boxY, width, 22, 3, 3, "FD");

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(100);
      doc.text(label, x + 4, boxY + 7);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(20);
      doc.text(String(value), x + 4, boxY + 16);
    };

    const drawTableHeader = (headers, widths) => {
      addPageIfNeeded(12);

      doc.setFillColor(35, 45, 65);
      doc.rect(margin, y, pageWidth - margin * 2, 8, "F");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(255);

      let x = margin + 2;

      headers.forEach((header, index) => {
        doc.text(header, x, y + 5.5);
        x += widths[index];
      });

      y += 8;
      doc.setTextColor(0);
    };

    const drawTableRow = (cells, widths, isTotal = false) => {
      addPageIfNeeded(9);

      if (isTotal) {
        doc.setFillColor(245, 247, 250);
        doc.rect(margin, y, pageWidth - margin * 2, 7, "F");
        doc.setFont("helvetica", "bold");
      } else {
        doc.setFont("helvetica", "normal");
      }

      doc.setFontSize(8);
      doc.setTextColor(25);

      let x = margin + 2;

      cells.forEach((cell, index) => {
        doc.text(String(cell), x, y + 5);
        x += widths[index];
      });

      y += 7;
    };

    const normaliseVatValue = (value) => {
      if (typeof value === "number") {
        return {
          net: 0,
          vat: value,
          gross: 0,
        };
      }

      return {
        net: Number(value?.net || 0),
        vat: Number(value?.vat || value?.amount || value || 0),
        gross: Number(value?.gross || value?.total || 0),
      };
    };

    // =========================
    // HEADER
    // =========================

    doc.setFillColor(24, 31, 50);
    doc.rect(0, 0, pageWidth, 32, "F");

    doc.setTextColor(255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("Accounting Report", margin, 14);

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(shopName || `Shop ${shopId || "N/A"}`, margin, 22);

    doc.setFontSize(8);
    doc.text(`Till: ${getSelectedTillLabel()}`, margin, 27);

    doc.setFontSize(9);
    doc.text(shopName || `Shop ${shopId || "N/A"}`, pageWidth - margin, 14, {
      align: "right",
    });

    doc.text(
      `${formatDate(report.reportFrom || fromDate)} to ${formatDate(
        report.reportTo || toDate,
      )}`,
      pageWidth - margin,
      22,
      { align: "right" },
    );

    y = 46;

    // =========================
    // SUMMARY BOXES
    // =========================

    const boxGap = 5;
    const boxWidth = (pageWidth - margin * 2 - boxGap * 2) / 3;

    drawSummaryBox("Gross Sales", money(grossSales), margin, y, boxWidth);

    drawSummaryBox(
      "Refunds",
      money(refundTotal),
      margin + boxWidth + boxGap,
      y,
      boxWidth,
    );

    drawSummaryBox(
      "Net Sales",
      money(salesTotal),
      margin + boxWidth * 2 + boxGap * 2,
      y,
      boxWidth,
    );

    y += 28;

    drawSummaryBox("Sales Transactions", saleTransactions, margin, y, boxWidth);

    drawSummaryBox(
      "Refund Transactions",
      refundTransactions,
      margin + boxWidth + boxGap,
      y,
      boxWidth,
    );

    drawSummaryBox(
      "Total Activity",
      totalTransactions,
      margin + boxWidth * 2 + boxGap * 2,
      y,
      boxWidth,
    );

    y += 28;

    drawSummaryBox("Net Cash", money(cashTotal), margin, y, boxWidth);

    drawSummaryBox(
      "Net Card",
      money(cardTotal),
      margin + boxWidth + boxGap,
      y,
      boxWidth,
    );

    drawSummaryBox(
      "DRS Deposit",
      money(drsTotal),
      margin + boxWidth * 2 + boxGap * 2,
      y,
      boxWidth,
    );

    y += 28;

    // =========================
    // PAYMENT SUMMARY
    // =========================

    drawSectionTitle("Payment Summary");

    drawTableHeader(["Payment Type", "Amount"], [120, 55]);

    drawTableRow(["Cash Sales", money(cashSales)], [120, 55]);

    drawTableRow(["Cash Refunds", `-${money(cashReturns)}`], [120, 55]);

    drawTableRow(["Net Cash", money(cashTotal)], [120, 55], true);

    drawTableRow(["Card Sales", money(cardSales)], [120, 55]);

    drawTableRow(["Card Refunds", `-${money(cardReturns)}`], [120, 55]);

    drawTableRow(["Net Card", money(cardTotal)], [120, 55], true);

    drawTableRow(
      ["Net Payments", money(cashTotal + cardTotal)],
      [120, 55],
      true,
    );

    // =========================
    // VAT BREAKDOWN
    // =========================

    // Start VAT on a fresh page so it is not split awkwardly.
    startNewPage();

    drawSectionTitle("VAT Breakdown");

    const vatEntries = Object.entries(report.vatSummary || {});

    drawTableHeader(["VAT Code", "Net", "VAT", "Gross"], [45, 45, 45, 45]);

    if (vatEntries.length === 0) {
      drawTableRow(["No VAT data found", "", "", ""], [45, 45, 45, 45]);
    } else {
      let totalVatNet = 0;
      let totalVat = 0;
      let totalVatGross = 0;

      vatEntries.forEach(([code, value]) => {
        const vat = normaliseVatValue(value);

        totalVatNet += vat.net;
        totalVat += vat.vat;
        totalVatGross += vat.gross;

        drawTableRow(
          [
            truncate(code, 18),
            money(vat.net),
            money(vat.vat),
            money(vat.gross),
          ],
          [45, 45, 45, 45],
        );
      });

      drawTableRow(
        ["TOTAL", money(totalVatNet), money(totalVat), money(totalVatGross)],
        [45, 45, 45, 45],
        true,
      );
    }

    // =========================
    // CATEGORY SUMMARY
    // =========================

    // =========================
    // CATEGORY SUMMARY
    // =========================

    const categoryEntries = Object.entries(mergedCategories || {});

    const categoryRequiredSpace =
      14 + // section title
      8 + // table header
      Math.max(categoryEntries.length, 1) * 7 +
      10; // total row and spacing

    addPageIfNeeded(categoryRequiredSpace);

    drawSectionTitle("Category Summary");

    drawTableHeader(
      ["Category", "Qty", "Net", "VAT", "Gross"],
      [60, 25, 35, 35, 35],
    );

    if (categoryEntries.length === 0) {
      drawTableRow(
        ["No category data found", "", "", "", ""],
        [60, 25, 35, 35, 35],
      );
    } else {
      let totalQty = 0;
      let totalNet = 0;
      let totalVat = 0;
      let totalGross = 0;

      categoryEntries.forEach(([cat, val]) => {
        const qty = Number(val.qty || 0);
        const net = Number(val.net || 0);
        const vat = Number(val.vat || 0);
        const gross = Number(val.gross || 0);

        totalQty += qty;
        totalNet += net;
        totalVat += vat;
        totalGross += gross;

        drawTableRow(
          [
            truncate(cat, 28),
            String(qty),
            money(net),
            money(vat),
            money(gross),
          ],
          [60, 25, 35, 35, 35],
        );
      });

      drawTableRow(
        [
          "TOTAL",
          String(totalQty),
          money(totalNet),
          money(totalVat),
          money(totalGross),
        ],
        [60, 25, 35, 35, 35],
        true,
      );
    }

    // =========================
    // FINAL TOTALS
    // =========================

    const finalTotalsRequiredSpace =
      14 + // section heading
      8 + // table heading
      8 * 7 +
      10;

    addPageIfNeeded(finalTotalsRequiredSpace);

    drawSectionTitle("Final Totals");

    drawTableHeader(["Description", "Amount"], [120, 55]);

    drawTableRow(["Gross Sales", money(grossSales)], [120, 55]);

    drawTableRow(["Returns / Refunds", `-${money(refundTotal)}`], [120, 55]);

    drawTableRow(["Net Sales", money(salesTotal)], [120, 55], true);

    drawTableRow(["DRS Deposit", money(drsTotal)], [120, 55]);

    drawTableRow(
      ["Net Sales Excluding DRS", money(salesExcludingDrs)],
      [120, 55],
    );

    drawTableRow(["Net Cash", money(cashTotal)], [120, 55]);

    drawTableRow(["Net Card", money(cardTotal)], [120, 55]);

    drawTableRow(
      ["Net Payments", money(cashTotal + cardTotal)],
      [120, 55],
      true,
    );

    addFooter();

    const tillFileLabel =
      selectedTillId === "all" ? "all-tills" : `till-${selectedTillId}`;

    const safeShopName = String(shopName || `shop-${shopId || "unknown"}`)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

    doc.save(
      `accounting-report-${safeShopName}-${tillFileLabel}-${
        fromDate || "from"
      }-to-${toDate || "to"}.pdf`,
    );

    // If you do not want report to disappear after download, remove these 3 lines.
    /*  setReport(null);
  setFromDate("");
  setToDate(""); */
  };

  const businessSummary = getBusinessSummary(report);

  return (
    <div className="reports-page">
      <div className="reports-header">
        <div>
          <h1 style={{ color: "#FFF" }}>Reports</h1>
          <p>
            View monthly sales, cash/card totals, VAT summaries and export
            reports for accounting.
          </p>
        </div>

        <div className="reports-badge">
          {getSelectedTillLabel()}
          {shopName ? ` | ${shopName}` : ""}
        </div>
      </div>

      <div className="reports-card">
        <div className="reports-card-header">
          <div>
            <h2>Generate Monthly Report</h2>
            <p>Select a date range to view sales performance for this shop.</p>
          </div>
        </div>

        <div className="report-filters">
          <div className="filter-group">
            <label>From Date</label>
            <span className="date-format-hint">DD/MM/YYYY</span>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>To Date</label>
            <span className="date-format-hint">DD/MM/YYYY</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Till</label>

            <span className="date-format-hint">
              Select one till or all tills
            </span>

            <select
              value={selectedTillId}
              onChange={(e) => {
                setSelectedTillId(e.target.value);
                setReport(null);
              }}
              disabled={tillsLoading}
              className="report-till-select"
            >
              <option value="all">All Tills</option>

              {tills.map((till) => (
                <option key={till.id} value={String(till.id)}>
                  {till.name} ({till.till_code})
                </option>
              ))}
            </select>
          </div>

          <button className="generate-report-btn" onClick={fetchReport}>
            Generate Report
          </button>
        </div>

        {error && <div className="report-error">{error}</div>}
      </div>

      {report && (
        <>
          <div className="summary-grid">
            <div className="summary-card total-card">
              <span>Gross Sales</span>

              <strong>
                €{Number(businessSummary.grossSales || 0).toFixed(2)}
              </strong>
            </div>

            <div className="summary-card">
              <span>Refunds</span>

              <strong>
                €{Number(businessSummary.refundTotal || 0).toFixed(2)}
              </strong>
            </div>

            <div className="summary-card">
              <span>Net Sales</span>

              <strong>
                €{Number(businessSummary.netSales || 0).toFixed(2)}
              </strong>
            </div>

            <div className="summary-card">
              <span>Cash Sales</span>

              <strong>
                €{Number(businessSummary.cashSales || 0).toFixed(2)}
              </strong>
            </div>

            <div className="summary-card">
              <span>Cash Refunds</span>

              <strong>
                €{Number(businessSummary.cashReturns || 0).toFixed(2)}
              </strong>
            </div>

            <div className="summary-card">
              <span>Net Cash</span>

              <strong>
                €{Number(businessSummary.netCash || 0).toFixed(2)}
              </strong>
            </div>

            <div className="summary-card">
              <span>Card Sales</span>

              <strong>
                €{Number(businessSummary.cardSales || 0).toFixed(2)}
              </strong>
            </div>

            <div className="summary-card">
              <span>Card Refunds</span>

              <strong>
                €{Number(businessSummary.cardReturns || 0).toFixed(2)}
              </strong>
            </div>

            <div className="summary-card">
              <span>Net Card</span>

              <strong>
                €{Number(businessSummary.netCard || 0).toFixed(2)}
              </strong>
            </div>

            <div className="summary-card">
              <span>Sales Transactions</span>

              <strong>{businessSummary.saleTransactions || 0}</strong>
            </div>

            <div className="summary-card">
              <span>Refund Transactions</span>

              <strong>{businessSummary.refundTransactions || 0}</strong>
            </div>

            <div className="summary-card">
              <span>Total Activity</span>

              <strong>{businessSummary.totalTransactions || 0}</strong>
            </div>
          </div>

          <div className="reports-actions-card">
            <div>
              <h3>Export Report</h3>
              <p>
                Download this monthly report as Excel or PDF for your records.
              </p>
            </div>

            <div className="report-buttons">
              <button className="excel-btn" onClick={downloadExcelReport}>
                Download Excel
              </button>

              <button className="pdf-btn" onClick={downloadPDFReport}>
                Download PDF
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ReportsPage;
